import React from 'react';
import { createHistory } from '@reach/router';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { camelCase } from 'change-case';
import { EventTrackingApp, MzTracking } from '#core/Tracking';
import Auth from '#core/Auth';
import Auth0 from '#core/Auth0';
import Environment from '#core/Environment';
import ApolloProvider from '#providers/ApolloProvider';
import LaunchDarklyProvider from '#providers/LaunchDarklyProvider';
import Permissions from '#core/Permissions';
import Institution from '#core/Institution';
import Years from '#core/Years';
import Products from '#core/Products';
import ErrorBoundary from '#coreComponents/ErrorBoundary';
import ErrorBoundaryFooter from '#coreComponents/ErrorBoundary/ErrorBoundaryFooter';
import AccessDeniedErrorScreen from '#coreComponents/ErrorBoundary/AccessDeniedErrorScreen';
import AppLoading, { FinishedLoading } from '#core/AppLoading';
import QueryBuilderProvider from '#core/QueryBuilderContext';
// import DevTooling from '#core/DevTooling';
import Router from './Router';
import AppUpdateNotification from '#core/AppUpdateNotification';
import NewRelic from '#core/NewRelic';
import LocationProviderWrapper from '#providers/LocationProviderWrapper';

const queryClient = new QueryClient();

const AuthenticatedApp = () => (
  // re-add LaunchDarklyProvider now we're authenticated
  <LaunchDarklyProvider>
    <ApolloProvider>
      {/* <DevTooling /> */}
      <AppUpdateNotification />
      <ErrorBoundary
        showDetailedErrorScreen
        ErrorComponent={({ footerComponent }) => (
          <AccessDeniedErrorScreen
            text="You do not have access to any institution data via the DataPoints platform"
            footerComponent={footerComponent}
          />
        )}
        footerComponent={ErrorBoundaryFooter}
        id="institution"
      >
        <Institution>
          <ErrorBoundary
            showDetailedErrorScreen
            footerComponent={ErrorBoundaryFooter}
            id="apolloProvider"
          >
            <QueryClientProvider client={queryClient}>
              <ApolloProvider>
                <Permissions>
                  <Years>
                    <Products>
                      <FinishedLoading>
                        <QueryBuilderProvider>
                          <ErrorBoundary
                            showDetailedErrorScreen
                            footerComponent={ErrorBoundaryFooter}
                            id="router"
                          >
                            <Router />
                          </ErrorBoundary>
                        </QueryBuilderProvider>
                      </FinishedLoading>
                    </Products>
                  </Years>
                </Permissions>
              </ApolloProvider>
            </QueryClientProvider>
          </ErrorBoundary>
        </Institution>
      </ErrorBoundary>
    </ApolloProvider>
  </LaunchDarklyProvider>
);

const UnAuthenticatedApp = () => (
  <FinishedLoading>
    <ErrorBoundary
      showDetailedErrorScreen
      footerComponent={ErrorBoundaryFooter}
      id="router"
    >
      <QueryClientProvider client={queryClient}>
        <Router />
      </QueryClientProvider>
    </ErrorBoundary>
  </FinishedLoading>
);

const App = () => {
  const flags = useFlags();
  const AuthComponent = flags[camelCase('auth0Enabled')] ? Auth0 : Auth;

  return (
    <Environment>
      <HelmetProvider>
        <NewRelic />
        <Helmet
          defaultTitle="THE DataPoints"
          titleTemplate="%s | THE DataPoints"
        />
        <LaunchDarklyProvider>
          <EventTrackingApp name="datapoints">
            <LocationProviderWrapper history={createHistory(window as any)}>
              <AppLoading>
                <ErrorBoundary
                  showDetailedErrorScreen
                  footerComponent={ErrorBoundaryFooter}
                  id="datapoints"
                >
                  <MzTracking />
                  <AuthComponent>
                    {({ isAuthenticated = false }) =>
                      isAuthenticated ? (
                        <AuthenticatedApp />
                      ) : (
                        <UnAuthenticatedApp />
                      )
                    }
                  </AuthComponent>
                </ErrorBoundary>
              </AppLoading>
            </LocationProviderWrapper>
          </EventTrackingApp>
        </LaunchDarklyProvider>
      </HelmetProvider>
    </Environment>
  );
};

export default App;
